import {
  ClockCircleOutlined,
  EditOutlined,
  FallOutlined,
  FileOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button } from '@mm/components';
import { useCanResolveDomain, useResolutionInfo } from '@mm/hooks';
import { useWebsiteReportSummaryActions } from '@mm/state/actions/WebsiteReportSummaryActions';
import {
  trafficTotalSelector,
  websiteSummarySelector,
} from '@mm/state/selectors/websiteReportSummarySelector';
import { InputNumber } from 'antd';
import g2 from 'assets/g2.svg';
import iconiqLogo from 'assets/iconiq-logo.png';
import metaLogo from 'assets/meta-partner.png';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TABLET_SMALL } from '../../../../editor/constants/screenSize';
import { BarGraph } from '../BarGraph/BarGraph';
import { CompetitorsAnalysis } from '../CompetitorsAnalysis/CompetitorsAnalysis';
import { HowXRayWorksVersionA } from '../HowXRayWorksVersionA/HowXRayWorksVersionA';
import { MissedLeadsTimer } from '../MissedLeadsTimer/MissedLeadsTimer';
import { MotivationalBlock } from '../MotivationalBlock/MotivationalBlock';
import { Partners } from '../Partners/Partners';
import { WebsiteScreenshot } from '../WebsiteScreenshot/WebsiteScreenshot';
import { WebsiteTrafficList } from '../WebsiteTrafficList/WebsiteTrafficList';
import { XRayCaptureDiagram } from '../XRayCaptureDiagram/XRayCaptureDiagram';
import { AVERAGE_WEB_TRAFFIC_GROWTH, beautifyNumber } from './constants';
import tableSrc from './Icons/table.png';
import { PdfReportButton } from './PdfReportButton/PdfReportButton';
import { ReportHeader } from './ReportHeader/ReportHeader';
import { LLRReportVersion } from './WebsiteReport';
import styles from './WebsiteReportSummaryVersionA.module.css';

// tslint:disable-next-line: cyclomatic-complexity
export const WebsiteReportSummaryVersionA = ({
  version,
  setVersion,
  websiteURL,
  openSignupModal,
  openEditUrlModal,
  isWhiteLabel,
}: {
  version: LLRReportVersion;
  setVersion: (version: LLRReportVersion) => void;
  websiteURL: string;
  openSignupModal: () => void;
  openEditUrlModal: () => void;
  isWhiteLabel?: boolean;
}) => {
  const { setTrafficTotals } = useWebsiteReportSummaryActions();

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= TABLET_SMALL;
  const dateNow = moment();

  const history = useHistory();
  const updateVersion = () => {
    setVersion('B');
    history.replace(history.location.pathname.replace(version, 'B'));
  };

  const totalTraffic = useSelector(trafficTotalSelector);
  const websiteSummary = useSelector(websiteSummarySelector);
  const organicTrafficTotal = totalTraffic.trafficType?.organic || 0;
  const paidTrafficTotal = totalTraffic.trafficType?.paid || 0;
  const auth = THUM_IO_AUTH_KEY ? `/auth/${THUM_IO_AUTH_KEY}` : '';
  const screenshotSrc = `https://image.thum.io/get${auth}/width/554/https://${websiteURL}`;
  const trafficSum = organicTrafficTotal + paidTrafficTotal;
  const missedTotalLeads =
    (organicTrafficTotal + paidTrafficTotal) * AVERAGE_WEB_TRAFFIC_GROWTH;

  const { bounceRate, pagesPerVisit, timeOnSite } = websiteSummary || {};

  const secondsInHour = 60 * 60;
  const avgVisitDuration = Number(timeOnSite);
  const avgDurationFormat =
    avgVisitDuration > secondsInHour ? 'HH:mm:ss' : 'mm:ss';

  const websiteData = [
    {
      icon: <TeamOutlined className={styles.dataBoxIcon} />,
      title: 'Estimated traffic',
      value: beautifyNumber(trafficSum),
      input: true,
    },
    {
      icon: <ClockCircleOutlined className={styles.dataBoxIcon} />,
      title: 'Avg. visit duration',
      value: avgVisitDuration
        ? `${moment.utc(avgVisitDuration * 1000).format(avgDurationFormat)}`
        : 'N/A',
    },
    {
      icon: <FallOutlined className={styles.dataBoxIcon} />,
      title: 'Bounce rate',
      value: bounceRate ? `${(Number(bounceRate) * 100).toFixed(2)}%` : 'N/A',
    },
    {
      icon: <FileOutlined className={styles.dataBoxIcon} />,
      title: 'Pages / Visit',
      value: pagesPerVisit ? `${Number(pagesPerVisit).toFixed(2)}` : 'N/A',
    },
  ];

  const updateTraffic = (inputValue: string | null): void => {
    const newTraffic = { ...totalTraffic };
    if (!newTraffic.trafficType) {
      newTraffic.trafficType = {};
    }

    newTraffic.trafficType.organic = parseFloat(
      inputValue || ''.replace(/,/g, ''),
    );
    newTraffic.trafficType.paid = 0;

    setTrafficTotals(newTraffic);
  };

  const { canResolveDomain } = useCanResolveDomain({
    websiteURL,
    initialValue: true,
  });

  return (
    <div className={classNames(styles.wrapper)}>
      {!isMobile && !isWhiteLabel && (
        <div className={styles.widget}>
          <img src={tableSrc} className={styles.widgetImage} />
          <Button
            onClick={updateVersion}
            color="secondary-blue"
            css={{ container: styles.widgetButton }}
          >
            View Meta Report
          </Button>
        </div>
      )}
      {!isWhiteLabel && (
        <ReportHeader
          activeVersion={version}
          setActiveVersion={setVersion}
          isMobile={isMobile}
          withLogos={false}
        />
      )}

      <div className={styles.container}>
        <div className={styles.topContent}>
          {!isWhiteLabel && (
            <div className={classNames(styles.row, styles.logos)}>
              <img src={metaLogo} className={styles.meta} />
              <img src={g2} className={styles.g2} />
              <img src={iconiqLogo} className={styles.logo} />
            </div>
          )}

          <section className={classNames(styles.mainPanel, styles.section)}>
            <div className={classNames(styles.column, styles.title)}>
              <div className={styles.heading}>
                <div className={styles.websiteUrl}>{websiteURL}</div>
                Automatic Email Capture Analysis
              </div>
              <div className={styles.subheading}>
                Based on our analysis, your website is capturing the contact
                information of 2% of your website visitors and ad traffic.
                <div style={{ marginTop: '20px' }}>
                  Our system will capture up to{' '}
                  <span className={styles.importantHighligted}>
                    20% of website visitor emails!
                  </span>
                </div>
              </div>
            </div>
            <div className={classNames(styles.row, styles.websiteOverview)}>
              <div className={styles.websiteUrl}>
                {websiteURL}{' '}
                <EditOutlined
                  className={styles.editIcon}
                  onClick={openEditUrlModal}
                />
              </div>

              <div className={styles.screenshotBlock}>
                <div>
                  <WebsiteScreenshot
                    canResolve={canResolveDomain}
                    websiteURL={websiteURL}
                    src={screenshotSrc}
                    css={{ image: styles.screenshot }}
                  />
                  <div className={styles.boxesContainer}>
                    {websiteData.map(item => (
                      <div className={styles.dataBox} key={item.title}>
                        {item.icon}
                        <div className={styles.dataBoxHeading}>
                          {item.title}
                          {item.input ? (
                            <InputNumber
                              maxLength={13}
                              controls={false}
                              value={item.value.toString()}
                              onChange={updateTraffic}
                              className={classNames(
                                styles.dataBoxValue,
                                styles.dataBoxInputContainer,
                              )}
                            />
                          ) : (
                            <span className={styles.dataBoxValue}>
                              {item.value}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <BarGraph
                  className={styles.barGraph}
                  buttonName="Get Started Free"
                  onClick={openSignupModal}
                  paidTraffic={paidTrafficTotal}
                  organicTraffic={organicTrafficTotal}
                  websiteUrl={websiteURL}
                  showTotal
                  isWhiteLabel={isWhiteLabel}
                />
              </div>
            </div>
          </section>
        </div>

        <section className={styles.section}>
          <MotivationalBlock
            websiteURL={websiteURL}
            onClick={openSignupModal}
            totalTraffic={trafficSum}
            isWhiteLabel={isWhiteLabel}
          />
        </section>

        <div className={styles.partnersWrapper}>
          <section className={classNames(styles.section, styles.marginTop24)}>
            <Partners />
          </section>
        </div>

        <section className={styles.section}>
          <CompetitorsAnalysis
            websiteURL={websiteURL}
            onClick={openSignupModal}
            isWhiteLabel={isWhiteLabel}
            traffic={trafficSum}
          />
        </section>

        <div className={styles.captureDiagram}>
          <section className={styles.section}>
            <XRayCaptureDiagram />
          </section>
        </div>

        <section className={styles.section}>
          <WebsiteTrafficList
            websiteURL={websiteURL}
            traffic={trafficSum}
            onClick={openSignupModal}
            isWhiteLabel={isWhiteLabel}
          />
        </section>

        <div className={styles.captureDiagram}>
          <section className={styles.section}>
            <HowXRayWorksVersionA
              websiteURL={websiteURL}
              currentTraffic={trafficSum}
              additionalLeads={missedTotalLeads}
              onClick={openSignupModal}
              src={screenshotSrc}
              canResolveDomain={canResolveDomain}
              isWhiteLabel={isWhiteLabel}
            />
          </section>
        </div>

        <section className={classNames(styles.timer, styles.section)}>
          <div className={classNames(styles.row, styles.timerContent)}>
            <div className={styles.left}>
              <div className={styles.note}>greatness awaits</div>
              <div className={styles.heading}>
                Every visitor is a potential lead!
              </div>
              <div className={styles.subHeading}>
                This clock was activated at {moment().format('h:mm:ss a')} on{' '}
                {moment(dateNow).format('MMMM Do YYYY')} and represents leads
                and their contact information you could have captured by by
                installing X-Ray technology on{' '}
                <span className={styles.websiteUrl}>{websiteURL}</span>
              </div>
            </div>
            <div className={styles.right}>
              <MissedLeadsTimer
                totalTraffic={totalTraffic}
                onClick={openSignupModal}
                buttonName="Get Started Free"
                css={{
                  button: styles.timerButton,
                  tooltip: styles.missedLeadsTooltip,
                }}
                withTooltip
              />
            </div>
          </div>
        </section>

        <PdfReportButton isWhiteLabel={isWhiteLabel} version={version} />
      </div>
    </div>
  );
};
